import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Gift } from "../types";
import { Socket } from "socket.io-client";
import { fetchUserBalance } from "../../../../../services/profileAPI";

/**
 * Custom hook to handle gift sending logic
 * @param socket - Socket instance
 * @param streamId - Current stream ID
 * @returns Gift handler state and functions
 */
export const useGiftHandler = (
  socket: Socket | null,
  streamId: string | undefined,
  receiverId: string | undefined // Add explicit receiver ID (username)
) => {
  const [userTokens, setUserTokens] = useState(0);
  const [sentGifts, setSentGifts] = useState<Gift[]>([]);
  const [showTokenPurchase, setShowTokenPurchase] = useState(false);
  const [isLoadingTokens, setIsLoadingTokens] = useState(true);

  // Fetch real user tokens
  const fetchTokens = useCallback(async () => {
    try {
      const loginData = await AsyncStorage.getItem("@loginData");
      if (!loginData) return;

      const parsedData = JSON.parse(loginData);
      // AccessToken might be capitalized or not depending on auth flow
      const accessToken = parsedData.AccessToken || parsedData.accessToken;

      if (accessToken) {
        const response = await fetchUserBalance(accessToken);
        if (response && response.body) {
          // Handle potential stringified body
          const body =
            typeof response.body === "string"
              ? JSON.parse(response.body)
              : response.body;
          console.log("body", body);

          // Field might be koCoins or tokens
          const balance = body.balance || 0;
          setUserTokens(Number(balance));
        }
      }
    } catch (error) {
      console.error("Failed to fetch user tokens:", error);
    } finally {
      setIsLoadingTokens(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  // Listen for new gifts (to show animation for everyone)
  useEffect(() => {
    if (!socket || !streamId) return;

    const handleNewGift = (data: any) => {
      // Logic for received gifts (animations etc)
    };

    socket.on("new-gift", handleNewGift);

    return () => {
      socket.off("new-gift", handleNewGift);
    };
  }, [socket, streamId]);

  const sendGift = useCallback(
    (gift: Gift) => {
      if (userTokens < gift.price) {
        setShowTokenPurchase(true);
        toast.error("Insufficient tokens");
        return;
      }

      if (socket && streamId) {
        // Optimistic update
        setUserTokens((prev) => prev - gift.price);
        setSentGifts((prev) => [...prev, { ...gift, timestamp: Date.now() }]);

        socket.emit(
          "gift",
          {
            giftType: "sticker",
            giftName: gift.giftName,
            giftValue: gift.price,
            receiverId: receiverId || streamId, // Use explicit receiver or fallback
          },
          (response: { success: boolean }) => {
            if (!response?.success) {
              // Revert/Re-fetch on failure
              toast.error("Gift failed to process");
              fetchTokens();
            } else {
              toast.success(`Sent ${gift.giftName}!`);
              // Optionally re-fetch to ensure sync
              // fetchTokens();
            }
          }
        );
      } else {
        toast.error("Connection error");
      }
    },
    [userTokens, socket, streamId, fetchTokens]
  );

  return {
    userTokens,
    sentGifts,
    sendGift,
    showTokenPurchase,
    setShowTokenPurchase,
    isLoadingTokens,
    refreshTokens: fetchTokens,
  };
};
